import { Instagram, Facebook, MapPin, Mail, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import fullscaleLogoWhite from "@/assets/fullscale-logo-white.png";
import { PRIORITY_INDUSTRIES } from "@/data/industries";

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Contact", href: "/contact" },
];

const serviceLinks = [
  { label: "Managed Websites", href: "/services/websites" },
  { label: "Google Profile & Local SEO", href: "/services/gbp-seo" },
  { label: "Reviews & Reputation", href: "/services/reputation" },
  { label: "Google Ads", href: "/services/lead-generation" },
  { label: "Lead Follow-Up & Automation", href: "/services/automation" },
  { label: "Past Customer Reactivation", href: "/services/reactivation" },
];


const serviceAreas = [
  "Sydney",
  "Melbourne",
  "Brisbane",
  "Perth",
  "Adelaide",
  "Regional Australia",
];

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="bg-[hsl(160_10%_5%)] relative"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="py-14 sm:py-16 lg:py-20">
        <div className="container-wide">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1.2fr_1.3fr] gap-10 lg:gap-8">
            {/* Brand */}
            <div>
              <Link
                to="/"
                className="inline-block mb-5"
                aria-label="FullScale Marketing - Back to home"
              >
                <img
                  src={fullscaleLogoWhite}
                  alt="FullScale Marketing"
                  className="h-9 sm:h-10 w-auto"
                  loading="lazy"
                />
              </Link>
              <p className="text-foreground-dark/65 text-base leading-[1.7] max-w-sm">
                FullScale Marketing helps Australian trade and local-service
                businesses improve their websites, Google visibility, reviews and
                lead follow-up.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-foreground-dark mb-4 text-base">
                Quick Links
              </h4>
              <nav className="space-y-3" aria-label="Quick links">
                {quickLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-foreground-dark/65 hover:text-primary transition-colors duration-300 text-base"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Core Services */}
            <div>
              <h4 className="font-semibold text-foreground-dark mb-4 text-base">
                Core Services
              </h4>
              <nav className="space-y-3" aria-label="Service links">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-foreground-dark/65 hover:text-primary transition-colors duration-300 text-base"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-foreground-dark mb-4 text-base">
                Contact
              </h4>
              <div className="space-y-3 text-foreground-dark/65 text-base mb-5">
                <a
                  href="mailto:info@fullscalemarketing.com.au"
                  className="flex items-start gap-2.5 hover:text-primary transition-colors break-words"
                >
                  <Mail className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span>info@fullscalemarketing.com.au</span>
                </a>
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span>Australia-wide — remote team</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 flex-shrink-0 mt-1" />
                  <span>Mon–Fri, 8:30am–5:30pm AEST</span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/fullscale_marketing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground-dark/65 hover:text-primary transition-colors"
                  aria-label="FullScale Marketing on Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/p/FullScale-Marketing-61562089223781/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground-dark/65 hover:text-primary transition-colors"
                  aria-label="FullScale Marketing on Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Service areas */}
          <div className="mt-10 pt-7 border-t border-foreground-dark/10">
            <p className="text-base text-foreground-dark/55 leading-[1.7]">
              <span className="font-semibold text-foreground-dark/80">
                Service areas:{" "}
              </span>
              {serviceAreas.join(" • ")}
            </p>
          </div>

          {/* Industries */}
          <div className="mt-5 pt-5 border-t border-foreground-dark/10">
            <p className="text-base text-foreground-dark/55 leading-[1.7]">
              <span className="font-semibold text-foreground-dark/80">
                Industries:{" "}
              </span>
              {PRIORITY_INDUSTRIES.join(" • ")}
            </p>
          </div>

          {/* Bottom */}
          <div className="pt-8 mt-8 border-t border-foreground-dark/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-base text-foreground-dark/50 text-center sm:text-left">
              © {currentYear} FullScale Marketing. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-base text-foreground-dark/50">
              <a href="#" className="hover:text-primary transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-primary transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
